/* Savings Goal Splitter. Turns one goal into a per-month and per-working-week number. */
import React, { useMemo, useState } from 'react';
import { PitCrewToolShell, ToolLabel, ToolInput, ToolApplyBar, COLORS } from './PitCrewToolShell';

const { INK } = COLORS;

const SavingsSplitterModal = ({ onApply, onClose, formatCurrency, workWeeks = 48, daysPerWeek = 5 }) => {
  const [goal, setGoal] = useState('');
  const [months, setMonths] = useState(12);

  const split = useMemo(() => {
    const amount = parseFloat(goal) || 0;
    if (amount <= 0) return null;
    const weeksInWindow = Math.max(1, Math.round((workWeeks * months) / 12));
    return {
      amount,
      perMonth: amount / months,
      perWeek: amount / weeksInWindow,
      perDay: amount / weeksInWindow / Math.max(1, daysPerWeek),
      weeksInWindow,
    };
  }, [goal, months, workWeeks, daysPerWeek]);

  return (
    <PitCrewToolShell
      eyebrow="Pit Crew tool"
      title="Savings Goal Splitter"
      lead="One big goal, broken into what it costs you each month and each working week. Nothing here is saved."
      onClose={onClose}
      testId="savings-splitter"
    >
      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <ToolLabel>What do you want to save?</ToolLabel>
          <ToolInput type="number" prefix="$" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="6,000" data-testid="savings-splitter-goal-input" />
        </div>
        <div>
          <ToolLabel>Over how many months?</ToolLabel>
          <div className="flex flex-wrap gap-2">
            {[3, 6, 9, 12].map((m) => (
              <button
                key={m}
                onClick={() => setMonths(m)}
                className="px-4 py-2.5 font-body text-sm transition-colors"
                style={{
                  background: months === m ? INK : '#FFFFFF',
                  color: months === m ? '#F2EFE7' : INK,
                  border: `1px solid ${months === m ? INK : '#E0D9CB'}`,
                  borderRadius: 0,
                }}
                data-testid={`savings-splitter-months-${m}`}
              >
                {m} months
              </button>
            ))}
          </div>
        </div>
      </div>

      {split && (
        <div className="p-6" style={{ background: '#FFFFFF', border: '1px solid #E8E2D5' }} data-testid="savings-splitter-result">
          <div className="grid sm:grid-cols-3 gap-5">
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: '#8B7E6A' }}>Each month</p>
              <p className="font-heading text-2xl font-medium" style={{ color: INK }}>{formatCurrency(split.perMonth)}</p>
            </div>
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: '#8B7E6A' }}>Each working week</p>
              <p className="font-heading text-2xl font-medium" style={{ color: INK }}>{formatCurrency(split.perWeek)}</p>
            </div>
            <div>
              <p className="font-body text-[11px] uppercase tracking-[0.18em] mb-1" style={{ color: '#8B7E6A' }}>Each working day</p>
              <p className="font-heading text-2xl font-medium" style={{ color: INK }}>{formatCurrency(split.perDay)}</p>
            </div>
          </div>
          <p className="font-body text-sm leading-relaxed mt-5 pt-4 border-t" style={{ color: '#5D5A6B', borderColor: '#E8E2D5' }}>
            That is {formatCurrency(split.amount)} spread across {split.weeksInWindow} working weeks, on top of
            everything else your target already covers.
          </p>
        </div>
      )}

      <ToolApplyBar
        testId="savings-splitter"
        applyLabel={split ? `Use ${formatCurrency(split.amount)} over ${months} months` : 'Use these numbers'}
        disabled={!split}
        note="This fills in your savings step, you can still change it."
        onApply={() => onApply({ savingsGoal: String(Math.round(split.amount)), savingsMonths: String(months) })}
        onClose={onClose}
      />
    </PitCrewToolShell>
  );
};

export default SavingsSplitterModal;
